'use client';

import type { ReactNode } from 'react';
import { ark } from '@ark-ui/react';
import { badge, HStack, Box, token } from '@terra/ui-styled-system';
import type { ComponentProps } from '@terra/ui-styled-system';
import { Text } from './text';
import { styled } from '@terra/ui-styled-system';

// Re-export other special badges
export {
  BadgeStatus,
  BADGE_STATUS,
  BADGE_STATUS_CONFIG,
  FINDING_STATUS_BADGE_STATUS_MAP,
  isMappedFindingStatus,
  type BadgeStatusProps,
  type BadgeStatusType,
} from './styled/badge-status';

export {
  BadgeMemoryStatus,
  BADGE_MEMORY_STATUS_CONFIG,
  type BadgeMemoryStatusProps,
} from './styled/badge-memory-status';

export {
  BadgeSeverity,
  BADGE_SEVERITY_CONFIG,
  isBadgeSeverityType,
  type BadgeSeverityProps,
  type BadgeSeverityType,
} from './styled/badge-severity';

export {
  BadgeVulnerability,
  type BadgeVulnerabilityProps,
  type BadgeVulnerabilityType,
} from './styled/badge-vulnerability';

export type BadgeVariant = 'fill' | 'outline' | 'emphasis';
type BadgeRootVariant = 'solid' | 'outline' | 'subtle';

export type BadgeSize = '2xs' | 'xs' | 'sm' | 'md' | 'lg';

type BadgeStyle = {
  bg?: string;
  color: string;
  borderColor?: string;
  borderWidth?: string;
  borderStyle?: string;
  iconColor?: string;
};

const BASIC_BADGE_STYLE: Record<BadgeVariant, BadgeStyle> = {
  fill: {
    bg: token('colors.neutral.3'),
    color: 'fg.default',
  },
  outline: {
    color: 'fg.default',
    borderColor: token('colors.border.neutral.muted'),
    borderWidth: '1px',
    borderStyle: 'solid',
  },
  emphasis: {
    color: 'fg.default',
    borderColor: token('colors.border.neutral.muted'),
    bg: token('colors.bg.neutral.subtle'),
    borderWidth: '1px',
    borderStyle: 'solid',
  },
};

export const variants: Record<BadgeVariant, BadgeRootVariant> = {
  fill: 'solid',
  outline: 'outline',
  emphasis: 'subtle',
};

type BadgeSizeConfig = {
  height: string;
  paddingX: number;
  contentGap: number;
  fontSize: string;
};

export const BADGE_SIZE_CONFIG: Record<BadgeSize, BadgeSizeConfig> = {
  '2xs': {
    height: token('lineHeights.2xs'),
    paddingX: 1,
    contentGap: 1,
    fontSize: 'xs',
  },
  xs: {
    height: '20px',
    paddingX: 1,
    contentGap: 1,
    fontSize: 'xs',
  },
  sm: {
    height: '22px',
    paddingX: 2,
    contentGap: 1,
    fontSize: 'xs',
  },
  md: {
    height: '24px',
    paddingX: 2.5,
    contentGap: 1.5,
    fontSize: 'sm',
  },
  lg: {
    height: '28px',
    paddingX: 3,
    contentGap: 1.5,
    fontSize: 'md',
  },
};

const Root = styled(ark.div, badge);

type BadgeRootProps = Omit<
  ComponentProps<typeof Root>,
  'children' | 'size' | 'variant'
>;

export type BadgeProps = {
  children: ReactNode;
  variant?: BadgeVariant;
  size?: BadgeSize;
  icon?: ReactNode;
  leadingContent?: ReactNode;
  bg?: string;
  color?: string;
  borderColor?: string;
  fontWeight?: ComponentProps<typeof Text>['fontWeight'];
  truncate?: boolean;
} & BadgeRootProps;

export const Badge = ({
  children,
  variant = 'fill',
  size = 'md',
  icon,
  leadingContent,
  bg,
  color,
  borderColor,
  fontWeight = 'normal',
  truncate,
  minW: minWProp,
  maxW: maxWProp,
  overflow: overflowProp,
  ...props
}: BadgeProps) => {
  const mappedVariant = variants[variant];
  const sizeConfig = BADGE_SIZE_CONFIG[size];
  const badgeColor = color ?? BASIC_BADGE_STYLE[variant].color;
  const shouldShowIcon = Boolean(icon);
  const iconElement = shouldShowIcon ? (
    <Box
      display="flex"
      alignItems="center"
      color={badgeColor}
      flexShrink={truncate ? 0 : undefined}
    >
      {icon}
    </Box>
  ) : null;
  const leadingElement = leadingContent ? (
    <Box display="flex" alignItems="center">
      {leadingContent}
    </Box>
  ) : null;

  return (
    <Root
      variant={mappedVariant}
      size={size === '2xs' || size === 'xs' ? 'sm' : size}
      height={sizeConfig.height}
      px={sizeConfig.paddingX}
      color={badgeColor}
      style={{
        backgroundColor: bg ?? BASIC_BADGE_STYLE[variant].bg,
        borderColor: borderColor ?? BASIC_BADGE_STYLE[variant].borderColor,
      }}
      borderWidth={BASIC_BADGE_STYLE[variant].borderWidth}
      borderStyle={BASIC_BADGE_STYLE[variant].borderStyle}
      data-bi-unmask="true"
      minW={truncate ? 0 : minWProp}
      maxW={truncate ? 'full' : maxWProp}
      overflow={truncate ? 'hidden' : overflowProp}
      {...props}
    >
      <HStack
        gap={sizeConfig.contentGap}
        alignItems="center"
        minW={truncate ? 0 : undefined}
        maxW={truncate ? 'full' : undefined}
      >
        {leadingElement}
        {iconElement}
        {children && (
          <Text
            as="span"
            fontSize={sizeConfig.fontSize}
            fontWeight={fontWeight}
            color={badgeColor}
            truncate={truncate}
            minW={truncate ? 0 : undefined}
          >
            {children}
          </Text>
        )}
      </HStack>
    </Root>
  );
};
